import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Check, X } from 'lucide-react'
import { SearchCombobox } from '../../components/ui/SearchCombobox'
import { Select } from '../../components/ui/Select'
import { initialsOf } from '../sales-team/salesTeamMetrics'
import type { StaffMember } from './leadCrmClient'
import {
  ALL_ASSIGNED,
  ANY,
  EMPTY_FILTERS,
  NAME_ORDER_LABELS,
  UNASSIGNED,
  type LeadFilterState,
  type NameOrder,
} from './leadFilterModel'

type Options = { accountManagers: string[]; priorities: string[]; stages: string[]; states: string[] }

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * The Leads filter sheet. Edits a draft and previews its result count; the
 * list only changes on "Show N leads".
 */
export function LeadFilterPanel({
  applied,
  options,
  staff,
  assignmentsReady,
  countFor,
  onApply,
  onClose,
}: {
  applied: LeadFilterState
  options: Options
  /** Staff that can appear as a salesperson filter: active, plus any with assignments. */
  staff: StaffMember[]
  assignmentsReady: boolean
  countFor: (filters: LeadFilterState) => number
  onApply: (filters: LeadFilterState) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState(applied)
  const [closing, setClosing] = useState(false)
  const timerRef = useRef<number | null>(null)
  const panelRef = useRef<HTMLElement>(null)
  const id = useId()

  const requestClose = useCallback((next?: LeadFilterState) => {
    if (timerRef.current !== null) return
    if (next) onApply(next)
    setClosing(true)
    timerRef.current = window.setTimeout(onClose, prefersReducedMotion() ? 0 : 180)
  }, [onApply, onClose])

  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    }
  }, [])

  useEffect(() => {
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [requestClose])

  // Keeps Tab inside the sheet while it is open.
  function trapFocus(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Tab' || !panelRef.current) return
    const focusable = [...panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)')]
    if (!focusable.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const set = <K extends keyof LeadFilterState>(key: K, value: LeadFilterState[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  const count = countFor(draft)

  return createPortal(
    <div className={`filter-sheet-layer${closing ? ' is-closing' : ''}`}>
      <button aria-label="Close filters" className="filter-sheet-backdrop" onClick={() => requestClose()} tabIndex={-1} type="button" />
      <section
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="filter-sheet"
        onKeyDown={trapFocus}
        ref={panelRef}
        role="dialog"
      >
        <header className="filter-sheet-header">
          <h2 id={`${id}-title`}>Filter leads</h2>
          <button aria-label="Close filters" className="icon-button" data-autofocus onClick={() => requestClose()} type="button">
            <X size={19} />
          </button>
        </header>

        <div className="filter-sheet-body">
          <fieldset className="filter-group">
            <legend>Ownership</legend>
            <div className="filter-field">
              <label htmlFor={`${id}-salesperson`}>Salesperson</label>
              <SearchCombobox
                disabled={!assignmentsReady}
                emptyText="No salesperson found"
                id={`${id}-salesperson`}
                onChange={(value) => set('salesperson', value)}
                options={[
                  { value: ANY, label: 'Anyone' },
                  { value: ALL_ASSIGNED, label: 'Any assigned' },
                  { value: UNASSIGNED, label: 'Unassigned' },
                  // Real people get a neutral avatar; the filter states above do not.
                  ...staff.map((member) => ({
                    value: member.id,
                    label: member.active ? member.displayName : `${member.displayName} (inactive)`,
                    icon: <span aria-hidden="true" className="wt-person-avatar">{initialsOf(member.displayName)}</span>,
                  })),
                ]}
                placeholder="Search salespeople"
                searchLabel="Search salespeople"
                value={draft.salesperson}
              />
              <p className="filter-hint">
                {assignmentsReady ? 'CRM assignment from Lead Detail.' : 'Assignments are unavailable right now.'}
              </p>
            </div>
            <div className="filter-field">
              <label htmlFor={`${id}-manager`}>Account manager</label>
              <Select
                id={`${id}-manager`}
                onChange={(value) => set('accountManager', value)}
                options={[{ value: ANY, label: 'Any account manager' }, ...options.accountManagers.map((name) => ({ value: name, label: name }))]}
                value={draft.accountManager}
              />
              <p className="filter-hint">From the Google Sheet.</p>
            </div>
          </fieldset>

          <fieldset className="filter-group">
            <legend>Pipeline</legend>
            {options.priorities.length ? (
              <div className="filter-field">
                <span className="filter-label" id={`${id}-priority`}>Priority</span>
                <div aria-labelledby={`${id}-priority`} className="filter-toggles" role="group">
                  {options.priorities.map((priority) => {
                    const on = draft.priorities.includes(priority)
                    return (
                      <button
                        aria-pressed={on}
                        className="filter-toggle"
                        key={priority}
                        onClick={() =>
                          set('priorities', on ? draft.priorities.filter((value) => value !== priority) : [...draft.priorities, priority])
                        }
                        type="button"
                      >
                        {priority}
                      </button>
                    )
                  })}
                </div>
              </div>
            ) : null}
            <div className="filter-field">
              <label htmlFor={`${id}-stage`}>Stage</label>
              <Select
                id={`${id}-stage`}
                onChange={(value) => set('stage', value)}
                options={[{ value: ANY, label: 'Any stage' }, ...options.stages.map((stage) => ({ value: stage, label: stage }))]}
                value={draft.stage}
              />
            </div>
            <div className="filter-field">
              <label htmlFor={`${id}-state`}>Location</label>
              <SearchCombobox
                emptyText="No location found"
                id={`${id}-state`}
                onChange={(value) => set('state', value)}
                options={[{ value: ANY, label: 'Any state' }, ...options.states.map((state) => ({ value: state, label: state }))]}
                placeholder="Search locations"
                searchLabel="Search locations"
                value={draft.state}
              />
            </div>
          </fieldset>

          <fieldset className="filter-group">
            <legend>Contact details</legend>
            <div className="filter-checks">
              {([['hasPhone', 'Has phone'], ['hasEmail', 'Has email'], ['hasSocial', 'Has social media']] as const).map(([key, label]) => (
                <label className="filter-check" key={key}>
                  <input checked={draft[key]} onChange={(event) => set(key, event.target.checked)} type="checkbox" />
                  <span aria-hidden="true" className="filter-check-box">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="filter-group">
            <legend>Sort</legend>
            <div className="filter-field">
              <label htmlFor={`${id}-order`}>Name order</label>
              <Select
                id={`${id}-order`}
                onChange={(value) => set('nameOrder', value as NameOrder)}
                options={(Object.keys(NAME_ORDER_LABELS) as NameOrder[]).map((order) => ({ value: order, label: NAME_ORDER_LABELS[order] }))}
                value={draft.nameOrder}
              />
            </div>
          </fieldset>
        </div>

        <footer className="filter-sheet-footer">
          <button className="lead-crm-action filter-clear" onClick={() => setDraft(EMPTY_FILTERS)} type="button">
            Clear all
          </button>
          <button className="button button-primary" onClick={() => requestClose(draft)} type="button">
            {count === 1 ? 'Show 1 lead' : `Show ${count.toLocaleString()} leads`}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  )
}
